import type { TermAndCondition } from '../types/timeSlots';

/**
 * Identity of a set of slot terms, ignoring the translated text.
 *
 * `text` is the only field the backend translates, so a language switch keeps
 * the identity while a different set of applicable terms changes it. Used to
 * decide whether previously accepted terms still refer to the same text.
 */
export function termsIdentity(terms: TermAndCondition[]): string {
  return terms.map((t) => `${t.area ?? ''}@${t.times.join(',')}`).join('|');
}

/**
 * Filter slot-level terms by selected time and area.
 *
 * Matches legacy AngularJS behaviour (personalize.js:58-70):
 * 1. Filter by time: keep terms whose `times` array includes selectedTime
 *    (or terms with an empty `times` array, which apply to all times).
 * 2. Area priority: if area-specific terms exist, use them exclusively;
 *    otherwise fall back to generic terms (no area).
 */
export function filterTerms(
  terms: TermAndCondition[],
  selectedTime: string | null,
  selectedAreaId: number | null,
): TermAndCondition[] {
  // Step 1: Filter by time
  let filtered = terms;
  if (selectedTime) {
    filtered = terms.filter(t => t.times.length === 0 || t.times.includes(selectedTime));
  }

  // Step 2: Area priority
  if (selectedAreaId != null) {
    const areaSpecific = filtered.filter(t => t.area === selectedAreaId);
    if (areaSpecific.length > 0) return areaSpecific;
  }

  // Fallback: generic terms (no area)
  return filtered.filter(t => !t.area);
}
